import type {
  DetectOverflowOptions,
  Middleware,
  MiddlewareData,
  Placement,
} from '@floating-ui/react-dom';
import {
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
} from '@floating-ui/react-dom';
import type { ContextType } from 'react';
import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useEffectEvent } from 'use-effect-event';
import ErrorBoundary from './ErrorBoundary';
import Slot from './Slot';
import * as styles from './Tether.module.scss';

const TetherBoundaryPadding = parseInt(styles.boundaryPadding, 10);

const TetherContext = createContext<{
  setArrow: (element: Element | null) => void;
  arrowData: MiddlewareData['arrow'];
  placement: Placement;
} | null>(null);

export const useTetherContext = () => {
  const context = useContext(TetherContext);
  if (!context) {
    if (process.env.NODE_ENV !== 'production') {
      throw new Error('TetherContext not found');
    }
    return {
      setArrow: () => {},
      arrowData: undefined,
      placement: 'top',
    } satisfies ContextType<typeof TetherContext>;
  }
  return context;
};

export const TetherIsInStorybookContext = createContext(false);

export interface TetherRef {
  anchorElement: HTMLElement | null;
  positionedElement: HTMLElement | null;
  /**
   * Updates the positioned view's position synchronously.
   *
   * Call this when the positioned view is hidden/revealed with CSS and its dimensions
   * remain unchanged, preventing ResizeObserver from detecting the change.
   */
  updatePositionedView: () => void;
}

type FlipBehavior = 'disabled' | 'sameAxisOnly' | 'allowCrossAxis';

interface TetherProps {
  children: React.ReactNode;
  positionedView: React.ReactNode;
  portalContainer?: HTMLElement;
  /**
   * The name of the error boundary that will wrap the positioned view.
   * When omitted, the positioned view renders without error boundary protection.
   * This prop is necessary because the positioned view uses Slot for rendering,
   * which ErrorBoundary does not support. We deliberately keep ErrorBoundary
   * simple without Slot support since it serves as our final safeguard
   * against UI errors.
   */
  errorBoundary?: string;
  placement?: Placement;
  flipBehavior?: FlipBehavior;
  offset?: number;
}

/**
 * A headless component that positions a view relative to its children using floating-ui.
 * It is functionally similar to the CSS anchor API but supports all browsers. It also
 * provides additional features, such as shifting, that would be difficult or impossible
 * to achieve with the CSS anchor API alone.
 *
 * Both the positioned view and the anchor (children) must be able to accept props and pass them
 * down to their underlying HTML elements, since they will receive refs, event handlers, etc.
 *
 * It defines a couple of CSS custom properties that can be used by the positioned view's
 * styling:
 * - properties.$tetherAvailableWidth: The width of the available space for the positioned view.
 *   Useful to be set as the max-width of the positioned view to allow shrinking before shifting
 *   or flipping. It's also useful at the same time to set a reasonable min-width to prevent
 *   the positioned view from being too narrow.
 * - properties.$tetherAvailableHeight
 * - properties.$tetherOffset
 *
 * @example
 * <Tether
 *   positionedView={<div>This will be positioned relative to the children</div>}
 *   placement="bottom"
 * >
 *   <div>Anchor</div>
 * </Tether>
 */
const Tether = forwardRef<TetherRef, TetherProps>(
  (
    {
      positionedView,
      children,
      portalContainer = document.body,
      errorBoundary,
      placement = 'bottom',
      flipBehavior = 'sameAxisOnly',
      offset: offsetAmount = 0,
    },
    ref,
  ) => {
    const isInStorybook = useContext(TetherIsInStorybookContext);

    const [arrowOptions, setArrowOptions] = useState<{
      element: Element;
      size: number;
    } | null>(null);

    const middleware = useMemo<(Middleware | undefined)[]>(() => {
      const detectOverflowOptions: DetectOverflowOptions = {
        padding: TetherBoundaryPadding,
        ...(isInStorybook
          ? {
              rootBoundary: 'document', // Prevent viewport height from affecting layout
            }
          : null),
      };

      const finalOffsetAmount = offsetAmount + (arrowOptions?.size ?? 0);
      return [
        finalOffsetAmount > 0 ? offset(finalOffsetAmount) : undefined,
        shift({ ...detectOverflowOptions }),
        size({
          ...detectOverflowOptions,
          apply(args) {
            // Sets up CSS custom properties for styling the positioned view.
            // These properties are set before the `flip` middleware to enable
            // the positioned view to attempt shrinking before flipping.
            // Inspired by https://floating-ui.com/docs/size#initialplacement
            const setProperty = (property: string, pixels: number) => {
              args.elements.floating.style.setProperty(property, `${pixels}px`);
            };
            setProperty(
              styles.propertyAvailableWidth,
              Math.max(args.availableWidth, 0),
            );
            setProperty(
              styles.propertyAvailableHeight,
              Math.max(args.availableHeight, 0),
            );
            setProperty(styles.propertyOffset, finalOffsetAmount);
          },
        }),
        flipBehavior === 'disabled'
          ? undefined
          : flip({
              ...detectOverflowOptions,
              fallbackAxisSideDirection:
                flipBehavior === 'allowCrossAxis' ? 'end' : 'none',
            }),
        arrow({ element: arrowOptions?.element ?? null, padding: 8 }),
      ];
    }, [flipBehavior, offsetAmount, arrowOptions, isInStorybook]);

    const {
      placement: actualPlacement,
      refs,
      floatingStyles,
      update,
      middlewareData: { arrow: arrowData },
    } = useFloating<HTMLElement>({
      placement,
      middleware,
      whileElementsMounted: autoUpdate,
    });

    useImperativeHandle(ref, () => ({
      updatePositionedView: update,
      anchorElement: refs.reference.current,
      positionedElement: refs.floating.current,
    }));

    const setArrow = useCallback((element: Element | null) => {
      setArrowOptions(element ? { element, size: element.clientHeight } : null);
    }, []);

    const arrowContextValue = useMemo(
      () => ({
        setArrow,
        arrowData,
        placement: actualPlacement,
      }),
      [setArrow, arrowData, actualPlacement],
    );

    const floatingNode = (
      <Slot ref={refs.setFloating} style={floatingStyles}>
        {positionedView}
      </Slot>
    );

    const onUsageCheck = useEffectEvent(() => {
      if (process.env.NODE_ENV === 'production') return;

      if (!refs.reference.current) {
        throw new Error(
          `Tether: Anchor element not found. This could be because:
          1. The anchor element is a React component that doesn't forward refs
          2. The anchor element is conditionally rendered
          
          Solution: If the anchor is conditionally rendered, wrap the entire Tether component in a conditional instead.`,
        );
      }

      if (React.Children.count(children) !== 1) {
        throw new Error('Tether: Anchor must have exactly one child.');
      }
    });

    useEffect(() => {
      onUsageCheck();
    }, []);

    return (
      <>
        <Slot ref={refs.setReference}>{children}</Slot>
        <TetherContext.Provider value={arrowContextValue}>
          {!!positionedView &&
            createPortal(
              errorBoundary ? (
                <ErrorBoundary name={errorBoundary}>
                  {floatingNode}
                </ErrorBoundary>
              ) : (
                floatingNode
              ),
              portalContainer,
            )}
        </TetherContext.Provider>
      </>
    );
  },
);

export default Tether;
